// ./src/components/features/CreateEventWizard/steps/QuestionsStep.tsx
//
// "What do you need to know from guests?" — the registration questions (ROADMAP A2b #62). Owner, 2026-09-29: an
// important step for EVERY event except a save-the-date — so it is shown whenever the setup rules leave the Questions
// feature available (a save-the-date takes no replies, and the seeded rule says so), not only when it was ticked.
//
// Questions are real rows on the event, made through the Questions page's own `QuestionDrawer` (never a reduced form)
// on the RSVP / ticket form (`askedOn: rsvp`). The one-tap starters PRE-FILL that drawer with a whole question (type,
// choices, who must answer), from the GENERATED library (`EVENT_QUESTION_STARTERS`, owned by eventsmagic-api):
// email, phone and dietary needs on EVERY event, then the ideas the event's type names (`questionIdeas`, seeded).
// ⚠⚠ THE FORMS ASK ONLY FOR FIRST AND LAST NAME (owner, 2026-09-29) — the step says so, because a host assumes an
//   RSVP collects an email and finds out on the day that it did not.
// ⓘ A guest who replies "No" is never asked (a question with no status condition is shown only when attending).
// ⚠ Four states, in order: not read yet → skeleton; FAILED → the error (never "no questions"); none → starters;
//   else the rows. ⚠ `rsvp_questions_qty` is a numeric ceiling counted over THIS list, which is complete (unpaginated)
//   — the same reasoning as the Questions page.

import React, { useMemo, useState } from 'react';
import { Stack, Group, Text, Button, Skeleton, Alert } from '@mantine/core';
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES } from '@/config';
import { useOptionalEventSetupCatalogContext } from '@/contexts';
import { useCreateGate, useRsvpQuestions, useToast } from '@/hooks';
import { QUESTION_ASKED_ON, RsvpQuestion, RsvpQuestionService } from '@/utils/api';
import { AppIcon, ConfirmModal, ErrorAlert, PlanFeatureNotice } from '@/components/generic';
import { EVENT_QUESTION_STARTERS, EVENT_QUESTION_STARTERS_ALWAYS, EventQuestionStarter } from '@/config/eventSetup.generated';
import { QuestionDrawer } from '@/pages/private/EventQuestionsPage/QuestionDrawer';
import type { QuestionPrefill } from '@/pages/private/EventQuestionsPage/questionPrefill';
import { reorderByDrag } from '@/utils/reorder';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { runBulkWrites, refusalReason } from '@/utils';

/** A starter as the drawer's prefill. */
const toPrefill = (q: EventQuestionStarter): QuestionPrefill => ({
  personField: q.personField,
  title: q.title,
  questionType: q.questionType,
  valueType: q.valueType,
  options: q.options,
  requiredScope: q.requiredScope,
  helpText: q.helpText,
  askOncePerGroup: q.askOncePerGroup,
});

/** An icon for a question already on the event: its starter's, else one for what it asks. */
const iconFor = (q: RsvpQuestion): string => {
  const starter = EVENT_QUESTION_STARTERS.find(
    (s) => (s.personField && s.personField === q.personField) || s.title.toLowerCase() === (q.title ?? '').trim().toLowerCase(),
  );

  if (starter) {
    return starter.icon;
  }

  switch (q.questionType) {
    case 'single_choice':
      return 'ListCheck';
    case 'multi_choice':
      return 'Checklist';
    case 'file':
      return 'Paperclip';
    case 'address':
      return 'MapPin';
    default:
      break;
  }

  const byValue: Record<string, string> = {
    email: 'Mail',
    phone: 'Phone',
    url: 'Link',
    date: 'Calendar',
    datetime: 'CalendarTime',
    time: 'Clock',
    boolean: 'Checkbox',
    integer: 'Hash',
    decimal: 'Hash',
    amount: 'Hash',
    age: 'Hash',
    rating: 'Star',
    long_text: 'AlignLeft',
  };

  return byValue[q.valueType ?? ''] ?? 'Forms';
};

export const QuestionsStep: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { draft, createdEventId: eventId, saving } = useEventWizard();
  const catalog = useOptionalEventSetupCatalogContext();

  const { questions, isLoaded, error, reload } = useRsvpQuestions(eventId, QUESTION_ASKED_ON.RSVP);
  const gate = useCreateGate(FEATURE_CODES.RSVP_QUESTIONS_QTY, {
    eventId,
    currentCount: isLoaded ? questions.length : undefined,
  });

  const [drawer, setDrawer] = useState<{ question: RsvpQuestion | null; prefill?: QuestionPrefill } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<RsvpQuestion | null>(null);
  const [removing, setRemoving] = useState(false);

  // The type's own ideas (a wedding's meal choice, a mixer's company and job title), then every event's starters.
  const type = catalog?.findType(draft.eventCategoryId) ?? null;
  // ⓘ Memoised: a fresh `[]` on every render would change what the memos below depend on (#129).
  const typeIdeas = useMemo(() => type?.offers?.find((o) => o.featureCode === 'questions')?.options?.questionIdeas ?? [], [type]);
  // ⚠ Two rows (owner, 2026-09-30: on a networking event the company / job title / "how did you hear about us" ideas were
  //   lost among the ones every event gets): the TYPE's own ideas first, named as suggestions for it, then the common ones.
  const { suggested, common } = useMemo(() => {
    const asked = (s: EventQuestionStarter) =>
      // Hide a starter the event already asks (same profile field, or the same wording).
      questions.some((q) => (s.personField && q.personField === s.personField) || (q.title ?? '').trim().toLowerCase() === s.title.toLowerCase());
    const pick = (ids: readonly string[]) =>
      ids
        .map((id) => EVENT_QUESTION_STARTERS.find((s) => s.id === id))
        .filter((s): s is EventQuestionStarter => !!s)
        .filter((s) => !asked(s));

    return {
      suggested: pick(typeIdeas.filter((id) => !EVENT_QUESTION_STARTERS_ALWAYS.includes(id))),
      common: pick(EVENT_QUESTION_STARTERS_ALWAYS),
    };
  }, [typeIdeas, questions]);
  const asksEmail = questions.some((q) => q.personField === 'email' || q.valueType === 'email');

  /** One tap = the question drawer, pre-filled with the whole question. */
  const renderStarter = (s: EventQuestionStarter, variant: 'light' | 'default') => (
    <Button
      key={s.id}
      variant={variant}
      size="md"
      leftSection={<AppIcon iconName={s.icon} size={16} />}
      disabled={!gate.canCreate || saving}
      onClick={() => openNew(toPrefill(s))}
    >
      {t(`events:wizard.questions.starter.${s.id}`, { defaultValue: s.title })}
    </Button>
  );

  const scopeLabel = (q: RsvpQuestion): string => {
    const scope = q.requiredScope ?? (q.isRequired ? 'all' : 'none');

    switch (scope) {
      case 'all':
        return t('events:wizard.questions.scopeAll', { defaultValue: 'Required for every guest' });
      case 'adults':
        return t('events:wizard.questions.scopeAdults', { defaultValue: 'Required for adults' });
      case 'primary':
        return t('events:wizard.questions.scopePrimary', { defaultValue: 'Required for the main guest' });
      default:
        return t('events:wizard.questions.scopeNone', { defaultValue: 'Optional' });
    }
  };

  const openNew = (prefill?: QuestionPrefill) => {
    if (!gate.canCreate) {
      return;
    }

    setDrawer({ question: null, prefill });
  };

  // The order guests are asked in: the server's, else the drop the host just made (while its write goes out).
  const [orderOverride, setOrderOverride] = useState<RsvpQuestion[] | null>(null);
  const [reordering, setReordering] = useState(false);
  const sortedQuestions = useMemo(
    () => [...questions].sort((a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0) || (a.createdAt ?? '').localeCompare(b.createdAt ?? '')),
    [questions],
  );
  const shown = orderOverride ?? sortedQuestions;

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await RsvpQuestionService.remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    await reload();
  };

  if (!eventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const { next, updates } = reorderByDrag(shown, result.source.index, result.destination.index);

    if (updates.length === 0) {
      return;
    }

    setOrderOverride(next);
    setReordering(true);

    const results = await runBulkWrites(updates, (u) => RsvpQuestionService.update(u.id, { orderNo: u.orderNo }));
    const failed = results.find((r) => !r.didSucceed());

    setReordering(false);

    // ⚠ A refused write is SAID, and the list goes back to what the server holds (never a order that was not saved).
    if (failed) {
      toast.error(refusalReason(failed) || t('events:wizard.questions.reorderFailed', { defaultValue: 'Could not save the new order.' }));
    }

    await reload();
    setOrderOverride(null);
  };

  const renderList = () => {
    if (!isLoaded) {
      return (
        <Stack gap="xs">
          <Skeleton height={56} radius="md" />
          <Skeleton height={56} radius="md" />
        </Stack>
      );
    }

    if (error) {
      return (
        <Stack gap="sm">
          <ErrorAlert
            error={error}
            title={t('events:wizard.questions.loadFailed', { defaultValue: 'Could not load the questions' })}
            page="Create event — questions"
            operation="rsvpQuestionList"
          />
          <Group>
            <Button variant="light" size="md" onClick={() => void reload()} leftSection={<AppIcon iconName="Refresh" size={16} />}>
              {t('common:retry', { defaultValue: 'Try again' })}
            </Button>
          </Group>
        </Stack>
      );
    }

    if (questions.length === 0) {
      return (
        <Text size="md" c="dimmed">
          {t('events:wizard.questions.none', {
            defaultValue: 'No questions yet — guests give their name and contact details only.',
          })}
        </Text>
      );
    }

    // ⓘ Drag by the grip to set the order guests are asked in (owner, 2026-09-30) — the Questions page's own rule.
    const canReorder = shown.length > 1 && !saving && !reordering;

    return (
      <DragDropContext onDragEnd={(result) => void onDragEnd(result)}>
        <Droppable droppableId="wizard-questions">
          {(provided) => (
            <Stack gap="xs" ref={provided.innerRef} {...provided.droppableProps}>
              {shown.map((q, index) => (
                <Draggable key={q.id} draggableId={String(q.id)} index={index} isDragDisabled={!canReorder}>
                  {(prov) => (
                    <div ref={prov.innerRef} {...prov.draggableProps}>
                      <WizardItemRow
                        icon={iconFor(q)}
                        title={q.title ?? ''}
                        subtitle={[
                          scopeLabel(q),
                          q.personField ? t('events:wizard.questions.onProfile', { defaultValue: 'saved to the guest’s profile' }) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        disabled={saving || reordering}
                        onEdit={() => setDrawer({ question: q })}
                        onRemove={() => setPendingRemove(q)}
                        dragHandleProps={shown.length > 1 ? prov.dragHandleProps : null}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.questions.intro', {
          defaultValue:
            'What do you need to know from each guest when they reply or buy? Guests who say they are not coming are not asked. Answers that belong on a guest’s profile (email, phone, dietary needs…) are saved there too.',
        })}
      </Text>

      {/* ⚠ The forms collect names only — said out loud, and louder while the event cannot email anyone. */}
      <Alert
        variant="light"
        color={asksEmail || !isLoaded ? 'gray' : 'yellow'}
        icon={<AppIcon iconName="InfoCircle" size={18} />}
      >
        <Text size="md">
          {t('events:wizard.questions.namesOnly', {
            defaultValue:
              'The form asks each guest for their first and last name only. To reach guests by email or phone, add those questions — they are the first suggestions below.',
          })}
        </Text>
      </Alert>

      <PlanFeatureNotice
        verdict={gate.verdict}
        onUpgrade={gate.goToUpgrade}
        what={t('events:wizard.questions.another', { defaultValue: 'more questions' })}
        gate="add"
      />

      <Group justify="space-between" wrap="nowrap">
        <Text fw={600} size="md">
          {t('events:wizard.questions.title', { defaultValue: 'Questions' })}
        </Text>
        <Button
          variant="light"
          size="md"
          leftSection={<AppIcon iconName="Plus" size={16} />}
          disabled={!gate.canCreate || saving}
          onClick={() => openNew()}
        >
          {t('events:wizard.questions.add', { defaultValue: 'Add a question' })}
        </Button>
      </Group>

      {isLoaded && !error && suggested.length > 0 ? (
        <Stack gap={6}>
          <Text size="md" fw={600}>
            {type?.name
              ? t('events:wizard.questions.suggestedFor', { defaultValue: 'Suggested for {{type}}', type: type.name })
              : t('events:wizard.questions.suggested', { defaultValue: 'Suggested for your event' })}
          </Text>
          <Group gap="xs">{suggested.map((s) => renderStarter(s, 'light'))}</Group>
        </Stack>
      ) : null}

      {isLoaded && !error && common.length > 0 ? (
        <Stack gap={6}>
          {suggested.length > 0 ? (
            <Text size="md" fw={600}>
              {t('events:wizard.questions.common', { defaultValue: 'Often asked' })}
            </Text>
          ) : null}
          <Group gap="xs">{common.map((s) => renderStarter(s, 'default'))}</Group>
        </Stack>
      ) : null}

      {renderList()}

      <QuestionDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        eventId={eventId}
        question={drawer?.question ?? null}
        prefill={drawer?.prefill ?? null}
        askedOn={QUESTION_ASKED_ON.RSVP}
        onSaved={() => void reload()}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.questions.removeTitle', { defaultValue: 'Remove this question?' })}
        message={t('events:wizard.tickets.removeMessage', {
          defaultValue: '“{{name}}” will be removed from this event.',
          name: pendingRemove?.title ?? '',
        })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};
